import * as React from 'react';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { CaseType, TApiCaseType, TCasesState } from '../../types/Cases';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useInspection } from '../../hooks/useInspection';
import { useSelector } from 'react-redux';
import { IAppState } from '../../store/Store';
import { TSurveysState } from '../../types/Survey';

interface ICaseTypeSelectProps {
  type: number | undefined;
  caseState: TCasesState;
  setType: (value: React.SetStateAction<number | undefined>) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      width: '100%',
      marginTop: theme.typography.pxToRem(16),
      marginBottom: theme.typography.pxToRem(16),
    },
  })
);

const CaseTypeSelect: React.FC<ICaseTypeSelectProps> = ({
  type,
  caseState,
  setType,
}): JSX.Element => {
  const classes = useStyles({});
  const { isInspectionStored } = useInspection();
  const surveysState = useSelector<IAppState, TSurveysState>(
    (state: IAppState) => state.surveysState
  );
  const allowedCaseTypes = surveysState.currentSurvey?.survey?.allowedCaseTypes;
  const { t } = useTranslation();

  const availableTypes = React.useMemo(() => {
    if (!caseState.data) return [];
    if (!isInspectionStored) return caseState.data;

    return caseState.data.filter((ct) => {
      if (ct.type === CaseType.WHISTLEBLOWER) return false;
      if (allowedCaseTypes && Array.isArray(allowedCaseTypes) && !allowedCaseTypes.includes(ct.type)) return false;
      return true;
    });
  }, [caseState.data, isInspectionStored, allowedCaseTypes]);

  React.useEffect(() => {
    if (!type && availableTypes.length > 0) {
      setType(availableTypes[0].type);
    }
  }, [availableTypes, type, setType]);

  return (
    <>
      {availableTypes.length >= 1 && (
        <FormControl variant='outlined' className={classes.formControl}>
          <InputLabel id='case-type-label'>
            {t('case.type.select.label')}
          </InputLabel>
          <Select
            labelId='case-type-label'
            id='case-type-select'
            value={type || availableTypes[0].type}
            onChange={(e) => setType(e.target.value as number)}
            label={t('case.type.select.label')}
          >
            {availableTypes.map((type: TApiCaseType, index: number) => (
              <MenuItem value={type.type} key={index}>
                {type.appForm
                  ? `${type.title} (${t(`case.type.app_form.${type.appForm}`)})`
                  : type.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </>
  );
};

export default React.memo(CaseTypeSelect);
